/**
 * Carrier Access — the carriers reaching a port, derived by the backend from
 * the related PortAllocation's description (`LM<CARRIER>` for last mile,
 * `CARR_<CARRIER>` for a backbone or trunk link).
 *
 * Shared by the ports list and the port detail card so both surfaces label and
 * order carriers identically. The backend already resolves the display `name`
 * (curated vendor, then the known-code map, then the raw code), so the UI
 * never maps codes itself.
 */

/**
 * Marker the backend read the carrier from, or `manual` for an Extended PoP
 * leg declared by hand (CTX-830).
 */
export type CarrierAccessSource = "lm" | "carr" | "manual";

/** Commercial type of a Carrier Access service (84-), as P3 sells it. */
export type CarrierAccessType = "dual" | "hybrid" | "satellite" | "single";

const ACCESS_TYPE_LABEL: Record<CarrierAccessType, string> = {
    dual: "Dual",
    hybrid: "Hybrid",
    satellite: "Satellite",
    single: "Single",
};

/**
 * Display label of the commercial type. Null is its own state — nobody has
 * captured the type yet — and must not read as "Single".
 */
export const accessTypeLabel = (type?: string | null): string =>
    ACCESS_TYPE_LABEL[type as CarrierAccessType] ?? "Not set";

/** Every commercial type, in label order, for a picker. */
export const ACCESS_TYPE_OPTIONS = Object.keys(
    ACCESS_TYPE_LABEL
) as CarrierAccessType[];

/** Medium of a carrier (CTX-800), captured in the Admin Center. */
export type CarrierMedium = "fiber" | "satellite";

/** Role of a leg in its Backhaul, captured in the Admin Center. */
export type LegPriority = "primary" | "secondary";

export interface CarrierAccess {
    /** Carrier mnemonic as networking wrote it, e.g. `IENTC`. */
    carrierCode: string;
    /** Resolved display name; falls back to `carrierCode`. */
    name: string;
    source: CarrierAccessSource;
    /**
     * Fiber or satellite, null while nobody captured it. Older backends send
     * it on Extended PoP legs only.
     */
    medium?: CarrierMedium | null;
    /** primary | secondary, null while nobody captured it. */
    priority?: LegPriority | null;
    /**
     * The sold Carrier Access (84-) the leg backs, null while nobody declared
     * it. Older backends send it on Extended PoP legs only.
     */
    carrierAccess?: CarrierAccessRef | null;
}

/** The sold Carrier Access a leg backs, as the Extended PoP views serve it. */
export interface CarrierAccessRef {
    id: number;
    serviceId: string;
    name?: string | null;
    accessType: string | null;
}

const MEDIUM_LABEL: Record<CarrierMedium, string> = {
    fiber: "Fiber Optic",
    satellite: "Satellite",
};

/** Every medium, in label order, for a picker. */
export const CARRIER_MEDIUMS = Object.keys(MEDIUM_LABEL) as CarrierMedium[];

/** "Fiber Optic" / "Satellite"; "Not set" while nobody captured it. */
export const carrierMediumLabel = (medium?: string | null): string =>
    MEDIUM_LABEL[medium as CarrierMedium] ?? "Not set";

/**
 * The medium to show next to a carrier, or null while nobody captured it:
 * the views then show the carrier alone rather than "Not set". The Admin
 * Center, where it is captured, keeps `carrierMediumLabel`.
 */
export const carrierMediumText = (medium?: string | null): string | null =>
    MEDIUM_LABEL[medium as CarrierMedium] ?? null;
// The backend also stores the circuit and service request for each carrier but
// does not send them, since nothing displays them yet. See
// `transformCarrierAccess` in the backend's ports `common-helpers.js`.

/**
 * Read the `carrierAccess` array off a raw port payload.
 *
 * Ports served by a backend that predates the feature have no such field, so
 * an absent or malformed value is normalized to an empty array — callers can
 * then treat "no carrier" and "field not deployed yet" the same way.
 */
export const shapeCarrierAccess = (raw: unknown): CarrierAccess[] => {
    const entries = (raw as { carrierAccess?: unknown } | null | undefined)
        ?.carrierAccess;
    if (!Array.isArray(entries)) return [];

    return entries
        .filter(
            (entry): entry is CarrierAccess =>
                typeof entry?.carrierCode === "string" &&
                entry.carrierCode.trim() !== ""
        )
        .map((entry) => ({
            carrierCode: entry.carrierCode,
            name: entry.name?.trim() || entry.carrierCode,
            source: entry.source,
            ...(entry.medium !== undefined ? { medium: entry.medium } : {}),
            ...(entry.priority !== undefined
                ? { priority: entry.priority }
                : {}),
            ...(entry.carrierAccess !== undefined
                ? { carrierAccess: entry.carrierAccess }
                : {}),
        }));
};

/**
 * Comma-separated carrier names for a read-only cell or detail row.
 *
 * Last mile is listed first: it is the carrier the customer's own access runs
 * over, so it is the one they came to the page for. Names are de-duplicated
 * because one carrier can be named by two different markers on the same port
 * (IENTC reaches port `10-C09582` as both its last mile and its trunk carrier),
 * and "IENTC, IENTC" would read as a bug.
 *
 * Returns an empty string when the port names no carrier, letting each surface
 * pick its own placeholder.
 */
export const formatCarrierAccessNames = (
    carriers: CarrierAccess[]
): string => {
    if (carriers.length === 0) return "";

    const lastMileFirst = [...carriers].sort((a, b) => {
        if (a.source === b.source) return a.name.localeCompare(b.name);
        return a.source === "lm" ? -1 : 1;
    });

    return [...new Set(lastMileFirst.map((carrier) => carrier.name))].join(
        ", "
    );
};

/** The legs of each sold access, by 84- id, and the legs nobody declared. */
const legsByAccess = (
    carriers: CarrierAccess[]
): {
    declared: { access: CarrierAccessRef; legs: CarrierAccess[] }[];
    undeclared: CarrierAccess[];
} => {
    const byAccess = new Map<number, { access: CarrierAccessRef; legs: CarrierAccess[] }>();
    const undeclared: CarrierAccess[] = [];
    for (const carrier of carriers) {
        const access = carrier.carrierAccess;
        if (!access) {
            undeclared.push(carrier);
            continue;
        }
        const group = byAccess.get(access.id) ?? { access, legs: [] };
        group.legs.push(carrier);
        byAccess.set(access.id, group);
    }
    const declared = Array.from(byAccess.values()).sort((a, b) =>
        a.access.serviceId.localeCompare(b.access.serviceId)
    );
    return { declared, undeclared };
};

/* ── Backhaul: the UI name of a sold Carrier Access ── */

const PRIORITY_LABEL: Record<LegPriority, string> = {
    primary: "Primary",
    secondary: "Secondary",
};

/** Every priority, in label order, for a picker. */
export const LEG_PRIORITIES = Object.keys(PRIORITY_LABEL) as LegPriority[];

/** "Primary" / "Secondary"; null while nobody captured it. */
export const priorityLabel = (priority?: string | null): string | null =>
    PRIORITY_LABEL[priority as LegPriority] ?? null;

const PRIORITY_RANK: Record<LegPriority, number> = { primary: 0, secondary: 1 };

/** Primary first, then secondary, then the legs nobody ranked; stable. */
export const sortLegsByPriority = <T extends { priority?: string | null }>(
    legs: readonly T[]
): T[] =>
    [...legs].sort(
        (a, b) =>
            (PRIORITY_RANK[a.priority as LegPriority] ?? 2) -
            (PRIORITY_RANK[b.priority as LegPriority] ?? 2)
    );

/**
 * Redundancy of a Backhaul from how many legs it has: one leg is 1+0, two
 * are 1+1, more are 1+N. Null with no leg, so nothing is claimed.
 */
export const backhaulRedundancy = (legCount: number): string | null => {
    if (legCount <= 0) return null;
    if (legCount === 1) return "Single · 1+0";
    if (legCount === 2) return "Dual · 1+1";
    return "1+N";
};

/**
 * Type of a Backhaul by what its legs ride on: all fiber is "Fiber", all
 * satellite "Satellite", a mix "Hybrid". How many legs it has is the
 * Redundancy, so the type never repeats "Dual".
 *
 * While a leg has no medium captured, the commercial type answers only when
 * it names a medium (`satellite`, `hybrid`); `dual` / `single` are counts,
 * already in the Redundancy, so the type stays null instead.
 */
export const backhaulType = (
    legs: { medium?: string | null }[],
    accessType?: string | null
): string | null => {
    const mediums = legs.map((leg) => leg.medium ?? null);
    if (mediums.length > 0 && mediums.every((medium) => medium !== null)) {
        const distinct = new Set(mediums);
        if (distinct.size > 1) return "Hybrid";
        return distinct.has("satellite") ? "Satellite" : "Fiber";
    }
    if (accessType === "satellite") return "Satellite";
    if (accessType === "hybrid") return "Hybrid";
    return null;
};

/**
 * One leg as "Primary: Fiber Optic · AXTEL". The priority or the medium is
 * left out while nobody captured it, never shown as "Not set".
 */
export const backhaulLegLine = (leg: {
    name: string;
    medium?: string | null;
    priority?: string | null;
}): string => {
    const carrier = [carrierMediumText(leg.medium), leg.name]
        .filter(Boolean)
        .join(" · ");
    const priority = priorityLabel(leg.priority);
    return priority ? `${priority}: ${carrier}` : carrier;
};

/** One Backhaul as the views show it, or the carriers nobody declared. */
export interface BackhaulGroup {
    access: CarrierAccessRef | null;
    /** "Dual · 1+1"; null for the undeclared carriers. */
    redundancy: string | null;
    /** One line per leg, primary first; identical lines read once. */
    lines: string[];
    /** The carrier names alone, in the same order, for a one-line view. */
    names: string[];
}

/** Carrier names, primary first, each once. */
const legNames = (legs: CarrierAccess[]): string[] => {
    const byName = [...legs].sort((a, b) => a.name.localeCompare(b.name));
    return [...new Set(sortLegsByPriority(byName).map((leg) => leg.name))];
};

/** Primary first, then secondary, then the rest; by carrier within each. */
const legLines = (legs: CarrierAccess[]): string[] => {
    const byName = [...legs].sort((a, b) => a.name.localeCompare(b.name));
    return [...new Set(sortLegsByPriority(byName).map(backhaulLegLine))];
};

/**
 * What a Backhaul field shows: each sold access (84-) with its redundancy
 * and its legs, once there is at least one; else the carriers nobody
 * declared yet. A site on a router that also carries backbone trunks (every
 * `CARR_` on it is a leg) would otherwise list them all under its 84-; those
 * stay visible in each CPE's links table.
 */
export const backhaulSummary = (carriers: CarrierAccess[]): BackhaulGroup[] => {
    const { declared, undeclared } = legsByAccess(carriers);
    if (declared.length > 0) {
        return declared.map((group) => ({
            access: group.access,
            redundancy: backhaulRedundancy(group.legs.length),
            lines: legLines(group.legs),
            names: legNames(group.legs),
        }));
    }
    if (undeclared.length === 0) return [];
    return [
        {
            access: null,
            redundancy: null,
            lines: legLines(undeclared),
            names: legNames(undeclared),
        },
    ];
};
